import { MessageSquarePlus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatConversationDate } from '@/lib/chatFormat'
import type { ChatConversationSummary } from '@/lib/chatTypes'
import { Skeleton } from '@/components/ui/skeleton'

export type ConversationListProps = {
  conversations: ChatConversationSummary[]
  loading?: boolean
  error?: string | null
  activeId: string | null
  onSelect: (id: string) => void
  onNew: () => void
  className?: string
}

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2'

export function ConversationList({
  conversations,
  loading,
  error,
  activeId,
  onSelect,
  onNew,
  className,
}: ConversationListProps) {
  return (
    <nav aria-label="Historique des conversations" className={cn('flex h-full min-h-0 flex-col', className)}>
      <div className="p-3">
        <button
          type="button"
          onClick={onNew}
          className={cn(
            'flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50',
            FOCUS_RING
          )}
        >
          <MessageSquarePlus aria-hidden="true" className="h-4 w-4" />
          Nouvelle conversation
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-3">
        {loading ? (
          <div className="space-y-2 px-1" aria-busy="true">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : error ? (
          <p className="px-2 py-3 text-sm text-red-700">{error}</p>
        ) : conversations.length === 0 ? (
          <p className="px-2 py-3 text-sm text-slate-500">Aucune conversation pour l'instant</p>
        ) : (
          <ul className="space-y-1">
            {conversations.map((c) => {
              const isActive = c.id === activeId
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(c.id)}
                    aria-current={isActive ? 'true' : undefined}
                    className={cn(
                      'flex w-full flex-col items-start rounded-md px-3 py-2 text-left hover:bg-slate-100',
                      isActive && 'bg-violet-50 text-violet-900 hover:bg-violet-100',
                      FOCUS_RING
                    )}
                  >
                    <span className="w-full truncate text-sm font-medium">
                      {c.title ?? 'Nouvelle conversation'}
                    </span>
                    <span className="text-xs text-slate-500">{formatConversationDate(c.updatedAt)}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </nav>
  )
}
